import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '@/shared/lib/i18n/use-language'
import { useUser } from '@/shared/hooks/use-user'
import type {
  UserGender,
  UserContactChannel,
} from '@/shared/types/global.types'

export type { UserGender, UserContactChannel }

interface UsePetStepTwoPageOptions {
  onContinue: () => void
  onBack: () => void
}

export function usePetStepTwoPage({
  onContinue,
  onBack,
}: UsePetStepTwoPageOptions) {
  const { t } = useTranslation()
  const { user, updatePreferences } = useUser()
  const { currentLanguage, updateLanguage } = useLanguage()

  const identifyOptions = useMemo(
    () => [
      {
        value: 'Woman' as UserGender,
        label: t('onboarding.pet.step2.identifyAsOptions.woman'),
      },
      {
        value: 'Man' as UserGender,
        label: t('onboarding.pet.step2.identifyAsOptions.man'),
      },
      {
        value: 'NonBinary' as UserGender,
        label: t('onboarding.pet.step2.identifyAsOptions.nonBinary'),
      },
      {
        value: 'PreferNotToSay' as UserGender,
        label: t('onboarding.pet.step2.identifyAsOptions.preferNot'),
      },
    ],
    [t],
  )
  const [identifyAs, setIdentifyAs] = useState<UserGender | ''>(
    user?.gender || '',
  )
  const [channels, setChannels] = useState<Set<UserContactChannel>>(
    new Set(user?.contactChannels || []),
  )

  useEffect(() => {
    if (!user) return
    setIdentifyAs(user.gender || '')
    setChannels(new Set(user.contactChannels || []))
  }, [user?.gender, user?.contactChannels])

  const toggleChannel = useCallback((channel: UserContactChannel) => {
    setChannels((prev) => {
      const next = new Set(prev)
      if (next.has(channel)) next.delete(channel)
      else next.add(channel)
      return next
    })
  }, [])

  const handleContinue = useCallback(() => {
    const contactChannels = Array.from(channels)
    const preferences = {
      ...(identifyAs && { gender: identifyAs }),
      ...(contactChannels.length > 0 && { contactChannels }),
    }

    if (Object.keys(preferences).length > 0) {
      updatePreferences(preferences)
    }

    onContinue()
  }, [channels, identifyAs, updatePreferences, onContinue])

  const isFormValid = identifyAs !== '' && channels.size > 0

  return {
    currentLanguage,
    updateLanguage,
    identifyAs,
    setIdentifyAs,
    identifyOptions,
    channels,
    toggleChannel,
    handleContinue,
    handleBack: onBack,
    isFormValid,
  }
}
